import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import dashboardImage from "../../assets/screenshots/dashboardscreenshot.png";
import productListImage from "../../assets/screenshots/productlist.png";
import assessmentStepsImage from "../../assets/screenshots/asssessment.png";
import resultspage from "../../assets/screenshots/resultspage.png";
import widget from "../../assets/screenshots/widget.png";

import "./OnboardingModal.css";

const STEPS = [
  {
    title: "Welcome to Zilch",
    subtitle: "Thank you for joining Zilch. Let's get you started.",
    videoSrc: "/welcome-animation.mp4",
    body: "Zilch helps you calculate a carbon footprint per SKU (kg CO₂e). This tour shows you how to add products, run an assessment in 5 steps, and publish results via the widget.",
    route: "/",
    routeLabel: "Start Tour",
  },
  {
    title: "Adding your products",
    subtitle: "Adding your products to Zilch to start assessing them",
    image: productListImage,
    body: (
      <>
        Start by getting your SKUs into Zilch. Add products one-by-one with +
        Add Product, or bulk import by emailing your product list to{" "}
        <a href="mailto:info@tryzilch.com">info@tryzilch.com</a>. Once a SKU is
        listed, hit Calculate to start its assessment.
      </>
    ),
    route: "/products",
    routeLabel: "Go to My Products",
  },
  {
    title: "Assessing your products",
    subtitle: "Understanding the assessment process",
    image: assessmentStepsImage,
    body: (
      <>
        Each product footprint is calculated in 5 steps:
        <ul>
          <li>
            <strong>Product info</strong>: confirm what the product is (SKU,
            category, unit).
          </li>
          <li>
            <strong>Materials</strong>: choose Quick (use Zilch’s custom product
            factors) or Detailed (enter precise material parts/weights).
          </li>
          <li>
            <strong>Packaging</strong>: add what ships with the product (bags,
            boxes, inserts).
          </li>
          <li>
            <strong>Manufacturing + transport</strong>: tell us how it moves
            through your supply chain.
          </li>
          <li>
            <strong>Results</strong>: get the total kg CO₂e per unit plus a
            breakdown by stage.
          </li>
        </ul>
        You can go back and edit any step at any time.
      </>
    ),

    route: "/products",
    routeLabel: "Start from Product List",
  },
  {
    title: "Welcome to your Dashboard",
    subtitle: "Dashboard overview",
    image: dashboardImage,
    body: "The Dashboard is your control panel: see how many SKUs are assessed, your completion %, and highlight high & low impact products. Use View Products to keep calculating or drill into a SKU.",
    route: "/",
    routeLabel: "Go to Dashboard",
  },
  {
    title: "Product Results",
    subtitle: "What the product results page can do for you",
    image: resultspage,
    body: "Results show the product footprint per unit (kg CO₂e) and where it comes from (materials, packaging, manufacturing, transport). Use this page to share the result, download reporting, or compare SKUs.",
    route: "/products",
    routeLabel: "View assessed products",
  },
  {
    title: "Connect your Zilch widget",
    subtitle:
      "Connect your Zilch widget to your storefront or partner integration",
    image: widget,
    body: "The widget displays a product’s footprint on your storefront or in a partner integration. To connect it: generate an API key in Profile, then follow the install steps. Once connected, any assessed SKU can display its footprint automatically.",
    route: "/profile",
    routeLabel: "Go to Profile",
  },
];

export default function OnboardingModal({ open, onFinish }) {
  const [stepIndex, setStepIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  const currentStep = useMemo(() => STEPS[stepIndex], [stepIndex]);
  const isLastStep = stepIndex === STEPS.length - 1;

  if (!open) return null;

  const handleNext = () => {
    if (isLastStep) {
      onFinish();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const handleRouteJump = () => {
    if (currentStep?.route) {
      navigate(currentStep.route);
    }
  };

  return (
    <div
      className="onboarding-modal-backdrop modal-dialog-scrollable"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboardingModalTitle"
    >
      <div className="onboarding-modal-card">
        <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
          <div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Step {stepIndex + 1} of {STEPS.length}
            </div>
            <h4 id="onboardingModalTitle" className="mb-0 fw-bold">
              {currentStep.title}
            </h4>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onFinish}
          >
            Skip tour
          </button>
        </div>

        <div className="ilch-text-grey" style={{ fontSize: 16 }}>
          {currentStep.subtitle}
        </div>

        {currentStep.videoSrc ? (
          <video
            src={currentStep.videoSrc}
            autoPlay
            
            muted
            playsInline
            className="img-fluid shadow border border-4 my-3 rounded p-2"
            style={{ width: "100%" }}
          />
        ) : (
          <img
            src={currentStep.image}
            alt={currentStep.title}
            className="img-fluid shadow border border-4 my-3 rounded p-2"
          />
        )}

        <p className="mb-4">{currentStep.body}</p>

        <div className="onboarding-progress mb-4" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`onboarding-progress-dot ${i <= stepIndex ? "active" : ""}`}
            />
          ))}
        </div>

        <div className="d-flex justify-content-between gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleBack}
            disabled={stepIndex === 0}
          >
            Back
          </button>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleRouteJump}
            >
              {currentStep.routeLabel}
            </button>
            <button
              type="button"
              className="zilch-green-button"
              onClick={handleNext}
            >
              {isLastStep ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
